import { createContext, useContext, useEffect, useState } from 'react';
import api, { isNativeApp } from '../api/axios';
import { clearCachedUser, getCachedUser, saveCachedUser } from '../offline/session';
import { loginOffline, rememberLogin } from '../offline/credentials';

const AuthContext = createContext(null);

function tokenExpiry(token) {
  try {
    const payload = JSON.parse(atob(String(token).split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return payload?.exp || null;
  } catch {
    return null;
  }
}

// توكن التطبيق الصادر قبل الجلسة الدائمة له تاريخ انتهاء: نستبدله بتوكن دائم ما دام صالحًا،
// فلا يُطلب من الموظف تسجيل الدخول مرة أخرى.
async function upgradeAppToken(token) {
  if (!isNativeApp() || !tokenExpiry(token)) return;
  try {
    const { data } = await api.post('/auth/refresh');
    if (data?.token && localStorage.getItem('token') === token) {
      localStorage.setItem('token', data.token);
    }
  } catch {
    // دون اتصال الآن: نحاول عند الفتح التالي مع الاتصال.
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchUser = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }

    try {
      const { data } = await api.get('/auth/user');
      setUser(data);
      saveCachedUser(data);
      upgradeAppToken(token);
    } catch (err) {
      // امسح التوكن فقط في حالة Unauthorized الحقيقية
      if (err?.response?.status === 401) {
        localStorage.removeItem('token');
        clearCachedUser();
        setUser(null);
      } else if (!err?.response) {
        // لا اتصال: نكمل بآخر بيانات مستخدم محفوظة على الجهاز حتى يعمل إدخال الفحوصات دون إنترنت.
        setUser(getCachedUser());
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUser();
  }, []);

  const login = async (username, password, auctionId = null) => {
    const cleanUsername = String(username || '').trim();
    const cleanPassword = String(password || '').trim();

    let data;
    try {
      ({ data } = await api.post('/auth/login', {
        username: cleanUsername,
        password: cleanPassword,
        auction_id: auctionId ? Number(auctionId) : null,
      }));
    } catch (err) {
      // لا اتصال بالخادم: التطبيق يدخل بآخر جلسة محفوظة لهذا المستخدم بعد التحقق من كلمة المرور.
      if (err?.response || !isNativeApp()) throw err;
      const session = await loginOffline(cleanUsername, cleanPassword, auctionId);
      localStorage.setItem('token', session.token);
      setUser(session.user);
      saveCachedUser(session.user);
      return { token: session.token, user: session.user, offline: true };
    }

    localStorage.setItem('token', data.token);
    setUser(data.user);
    // رد الدخول لا يحمل قائمة المزادات كاملة؛ نحفظ النسخة الكاملة من /auth/user.
    saveCachedUser(data.user);
    api.get('/auth/user').then(({ data: fullUser }) => {
      saveCachedUser(fullUser);
      if (isNativeApp()) {
        rememberLogin({ username: cleanUsername, password: cleanPassword, token: data.token, user: fullUser });
      }
    }).catch(() => {});
    return data;
  };

  const logout = async () => {
    try {
      await api.post('/auth/logout');
    } catch {
      // ignore network/token errors on logout
    }
    localStorage.removeItem('token');
    clearCachedUser();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refreshUser: fetchUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
